import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
  Input
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GetLicenceUsageGQL, GetLicenceUsageQuery } from '@polylearn/models';
import { BehaviorSubject } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import * as XLSX from 'xlsx';

interface InstitutionUsage {
  name: string;
  parentInstitution: string | null;
  totalRegistrations: number;
  registrations: Array<{
    institutionName: string;
    level: string;
    created_at: any;
    email: string;
    creatorEmail: string;
  }>;
}

@Component({
  selector: 'polylearn-see-credits',
  templateUrl: './see-credits.component.html',
  styleUrls: ['./see-credits.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' })
      ),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ]
})
export class SeeCreditsComponent implements OnInit, AfterViewInit {
  @Input() role: string = 'limitedAdmin';

  dateRangeForm: FormGroup;
  institutionsData = new MatTableDataSource<InstitutionUsage>([]);
  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string | null>(null);
  displayedColumns: string[] = [
    'name',
    'parentInstitution',
    'totalRegistrations',
    'actions'
  ];
  expandedInstitution: InstitutionUsage | null = null;

  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  constructor(
    private getLicenceUsageGQL: GetLicenceUsageGQL,
    private cdr: ChangeDetectorRef
  ) {
    // Initialiser les dates par défaut : début et fin du mois en cours
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.dateRangeForm = new FormGroup({
      startDate: new FormControl(startOfMonth),
      endDate: new FormControl(endOfMonth)
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.institutionsData.sort = this.sort;

    // Configurer le tri personnalisé pour les différentes colonnes
    this.institutionsData.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name':
          return item.name;
        case 'parentInstitution':
          return item.parentInstitution || '';
        case 'totalRegistrations':
          return item.totalRegistrations;
        default:
          return (item as any)[property];
      }
    };

    this.cdr.detectChanges();
  }

  loadData(): void {
    const { startDate, endDate } = this.dateRangeForm.value;

    if (!startDate || !endDate) {
      this.error$.next('Veuillez sélectionner une plage de dates valide');
      return;
    }

    this.loading$.next(true);
    this.error$.next(null);

    // Formater les dates en ISO sans conversion de fuseau horaire
    const formatDateToISO = (date: Date, isEndOfDay: boolean): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      if (isEndOfDay) {
        return `${year}-${month}-${day}T23:59:59Z`;
      } else {
        return `${year}-${month}-${day}T00:00:00Z`;
      }
    };

    this.getLicenceUsageGQL
      .fetch(
        {
          startDate: formatDateToISO(startDate, false),
          endDate: formatDateToISO(endDate, true)
        },
        {
          fetchPolicy: 'no-cache',
          context: {
            headers: {
              role: this.role
            }
          }
        }
      )
      .subscribe({
        next: result => {
          const allInstitutions = this.transformData(result.data);

          // Regrouper par établissement parent
          const parentMap = new Map<string, InstitutionUsage>();

          allInstitutions.forEach(inst => {
            // Déterminer la clé de regroupement (nom du parent ou nom de l'institution si pas de parent)
            const parentKey = inst.parentInstitution || inst.name;

            if (!parentMap.has(parentKey)) {
              // Créer l'entrée parent
              parentMap.set(parentKey, {
                name: parentKey,
                parentInstitution: null,
                totalRegistrations: 0,
                registrations: []
              });
            }

            const parent = parentMap.get(parentKey)!;

            // Ajouter les registrations avec le nom de l'établissement
            inst.registrations.forEach(reg => {
              parent.registrations.push({
                institutionName: inst.name,
                level: reg.level,
                created_at: reg.created_at,
                email: reg.email,
                creatorEmail: reg.creatorEmail
              });
            });

            parent.totalRegistrations += inst.totalRegistrations;
          });

          this.institutionsData.data = Array.from(parentMap.values());
          this.loading$.next(false);
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Erreur lors du chargement des données:', error);
          this.error$.next('Erreur lors du chargement des données');
          this.loading$.next(false);
          this.cdr.markForCheck();
        }
      });
  }

  private transformData(data: GetLicenceUsageQuery): InstitutionUsage[] {
    return (data.institutions || []).map(institution => ({
      name: institution.name,
      parentInstitution: institution.parent_institution?.name || null,
      totalRegistrations:
        institution.registrations_aggregate?.aggregate?.count || 0,
      registrations: (institution.registrations || []).map(reg => ({
        institutionName: institution.name,
        level: reg.level,
        created_at: reg.created_at,
        email: reg.email,
        creatorEmail: reg.creator?.email || ''
      }))
    }));
  }

  private getPricing(level: string) {
    const levelUpper = level?.toUpperCase() || '';

    // Junior levels
    if (levelUpper.includes('JR') || levelUpper.includes('JUNIOR')) {
      if (levelUpper.includes('A1')) {
        return {
          ean: '9782090346589',
          libelle: 'Plateforme ABC DELF JR A1',
          prixTTC: 44.4,
          prixHT: 37.03
        };
      }
      if (levelUpper.includes('A2')) {
        return {
          ean: '9782090346619',
          libelle: 'Plateforme ABC DELF JR A2',
          prixTTC: 45.6,
          prixHT: 38.01
        };
      }
      if (levelUpper.includes('B1')) {
        return {
          ean: '9782090346633',
          libelle: 'Plateforme ABC DELF JR B1',
          prixTTC: 46.8,
          prixHT: 38.99
        };
      }
      if (levelUpper.includes('B2')) {
        return {
          ean: '9782090346657',
          libelle: 'Plateforme ABC DELF JR B2',
          prixTTC: 48.0,
          prixHT: 39.97
        };
      }
    }

    // Adult levels
    if (levelUpper === 'A1') {
      return {
        ean: '9782090346596',
        libelle: 'Plateforme ABC DELF A1',
        prixTTC: 31.2,
        prixHT: 25.97
      };
    }
    if (levelUpper === 'A2') {
      return {
        ean: '9782090346602',
        libelle: 'Plateforme ABC DELF A2',
        prixTTC: 32.4,
        prixHT: 27.02
      };
    }
    if (levelUpper === 'B1') {
      return {
        ean: '9782090346626',
        libelle: 'Plateforme ABC DELF B1',
        prixTTC: 33.6,
        prixHT: 28.0
      };
    }
    if (levelUpper === 'B2') {
      return {
        ean: '9782090346640',
        libelle: 'Plateforme ABC DELF B2',
        prixTTC: 34.9,
        prixHT: 29.05
      };
    }

    return { ean: '', libelle: '', prixTTC: 0, prixHT: 0 };
  }

  private getHeaders(): string[] {
    if (this.role === 'manager') {
      return [
        'Établissement',
        'Établissement Parent',
        'Total Inscriptions',
        'Niveau',
        'Date',
        'Email',
        'Créateur'
      ];
    }
    return [
      'Établissement',
      'Établissement Parent',
      'Total Inscriptions',
      'Niveau',
      'Date',
      'Email',
      'Créateur',
      'ISBN/EAN',
      'Libellé',
      'Prix TTC EUR',
      'Prix HT EUR'
    ];
  }

  private getColumnConfig() {
    if (this.role === 'manager') {
      return [
        { wch: 30 }, // Institution
        { wch: 25 }, // Institution Parente
        { wch: 15 }, // Total Inscriptions
        { wch: 10 }, // Niveau
        { wch: 12 }, // Date
        { wch: 30 }, // Email
        { wch: 30 } // Créateur
      ];
    }
    return [
      { wch: 30 }, // Institution
      { wch: 25 }, // Institution Parente
      { wch: 15 }, // Total Inscriptions
      { wch: 10 }, // Niveau
      { wch: 12 }, // Date
      { wch: 30 }, // Email
      { wch: 30 }, // Créateur
      { wch: 15 }, // ISBN/EAN
      { wch: 30 }, // Libellé
      { wch: 12 }, // Prix TTC
      { wch: 12 } // Prix HT
    ];
  }

  private createRowsFromInstitutions(institutions: InstitutionUsage[]): any[] {
    const rows: any[] = [];
    institutions.forEach(inst => {
      inst.registrations.forEach(reg => {
        if (this.role === 'manager') {
          rows.push([
            reg.institutionName,
            reg.institutionName === inst.name ? '' : inst.name,
            inst.totalRegistrations,
            reg.level,
            new Date(reg.created_at).toLocaleDateString('fr-FR'),
            reg.email,
            reg.creatorEmail
          ]);
        } else {
          const pricing = this.getPricing(reg.level);
          rows.push([
            reg.institutionName,
            reg.institutionName === inst.name ? '' : inst.name,
            inst.totalRegistrations,
            reg.level,
            new Date(reg.created_at).toLocaleDateString('fr-FR'),
            reg.email,
            reg.creatorEmail,
            "'" + pricing.ean,
            pricing.libelle,
            pricing.prixTTC,
            pricing.prixHT
          ]);
        }
      });
    });
    return rows;
  }

  private createWorksheet(rows: any[]): XLSX.WorkSheet {
    // Pour le manager, pas de résumé de ventes
    if (this.role === 'manager') {
      const allData = [this.getHeaders(), ...rows];
      const ws = XLSX.utils.aoa_to_sheet(allData);
      ws['!cols'] = this.getColumnConfig();
      return ws;
    }

    // Calculer le résumé par niveau
    const levelSummary = new Map<
      string,
      { ean: string; libelle: string; prixHT: number; qty: number }
    >();

    rows.forEach(row => {
      const level = row[3]; // Niveau
      const ean = row[7].replace("'", ''); // ISBN/EAN sans le préfixe '
      const libelle = row[8]; // Libellé
      const prixHT = row[10]; // Prix HT (déjà un nombre)

      if (!levelSummary.has(level)) {
        levelSummary.set(level, { ean, libelle, prixHT, qty: 0 });
      }
      levelSummary.get(level)!.qty++;
    });

    // Construire les lignes de résumé
    const summaryRows: any[] = [];
    const dataEndRow = rows.length + 1; // +1 pour la ligne d'en-tête
    summaryRows.push(['']); // Ligne vide
    summaryRows.push([
      'ISBN',
      'TITRE',
      'QTÉ',
      'PRIX HT',
      'REMISE',
      'PRIX NET HT',
      'MONTANT'
    ]);

    let currentSummaryRow = dataEndRow + 3; // +2 pour ligne vide et en-tête, +1 pour démarrer à la première ligne de données
    const summaryStartRow = currentSummaryRow;

    levelSummary.forEach(({ ean, libelle, prixHT, qty }) => {
      summaryRows.push([
        "'" + ean,
        libelle,
        qty,
        prixHT, // Nombre réel pour que les formules fonctionnent
        0, // Remise par défaut à 0 (nombre pour les formules)
        { f: `D${currentSummaryRow}*(1-E${currentSummaryRow})` }, // Formule: PRIX HT * (1 - REMISE)
        { f: `C${currentSummaryRow}*F${currentSummaryRow}` } // Formule: QTÉ * PRIX NET HT
      ]);
      currentSummaryRow++;
    });

    const summaryEndRow = currentSummaryRow - 1;
    summaryRows.push([
      '',
      '',
      '',
      '',
      '',
      'TOTAL',
      { f: `SUM(G${summaryStartRow}:G${summaryEndRow})` } // Formule: Somme des montants
    ]);

    // Combiner les données et le résumé
    const allData = [this.getHeaders(), ...rows, ...summaryRows];
    const ws = XLSX.utils.aoa_to_sheet(allData);
    ws['!cols'] = this.getColumnConfig();
    return ws;
  }

  exportToExcel(): void {
    const institutions = this.institutionsData.data;
    const timestamp = new Date().toISOString().split('T')[0];
    const wb = XLSX.utils.book_new();

    // Feuille Global
    const allRows = this.createRowsFromInstitutions(institutions);
    const wsGlobal = this.createWorksheet(allRows);
    XLSX.utils.book_append_sheet(wb, wsGlobal, 'Global');

    // Identifier les institutions qui sont des parents
    const parentInstitutionNames = new Set<string>();
    institutions.forEach(inst => {
      if (inst.parentInstitution) {
        parentInstitutionNames.add(inst.parentInstitution);
      }
    });

    // Créer une feuille par institution (uniquement parents ou sans parent)
    const usedSheetNames = new Set<string>(['Global']);
    institutions.forEach(inst => {
      if (!inst.registrations || inst.registrations.length === 0) {
        return;
      }

      const isParent = parentInstitutionNames.has(inst.name);
      const hasNoParent = !inst.parentInstitution;

      if (!isParent && !hasNoParent) {
        return;
      }

      const instRows = this.createRowsFromInstitutions([inst]);
      const wsInst = this.createWorksheet(instRows);

      let baseName = inst.name
        .replace(/[^a-z0-9 ]/gi, '')
        .trim()
        .substring(0, 27);

      if (!baseName) {
        baseName = 'Institution';
      }

      let sanitizedName = baseName;
      let counter = 1;

      while (usedSheetNames.has(sanitizedName)) {
        sanitizedName = `${baseName}_${counter}`;
        counter++;
      }

      usedSheetNames.add(sanitizedName);
      XLSX.utils.book_append_sheet(wb, wsInst, sanitizedName);
    });

    XLSX.writeFile(wb, `licence-usage-${timestamp}.xlsx`);
  }

  onDateChange(): void {
    this.loadData();
  }

  toggleDetails(institution: InstitutionUsage): void {
    this.expandedInstitution =
      this.expandedInstitution === institution ? null : institution;
    this.cdr.markForCheck();
  }

  exportInstitutionToExcel(institution: InstitutionUsage): void {
    const timestamp = new Date().toISOString().split('T')[0];
    const wb = XLSX.utils.book_new();

    const rows = this.createRowsFromInstitutions([institution]);
    const ws = this.createWorksheet(rows);

    XLSX.utils.book_append_sheet(wb, ws, 'Données');

    const sanitizedName = institution.name
      .replace(/[^a-z0-9 ]/gi, '')
      .trim()
      .substring(0, 50);
    const filename = `${sanitizedName || 'institution'}-${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);
  }
}
